import React from 'react';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

export interface SelectableCardProps {
  id: string;
  title: string;
  description?: string;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconContainerClassName?: string;
  badge?: string;
  className?: string;
  /** Single-select uses radio semantics; multiple uses checkbox. Default: single. */
  selectionMode?: 'single' | 'multiple';
}

export function SelectableCard({
  id,
  title,
  description,
  selected,
  onSelect,
  disabled = false,
  icon,
  iconContainerClassName,
  badge,
  className,
  selectionMode = 'single',
}: SelectableCardProps) {
  const isMultiple = selectionMode === 'multiple';

  return (
    <button
      type="button"
      id={id}
      role={isMultiple ? 'checkbox' : 'radio'}
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'relative flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        selected
          ? 'border-primary-500 bg-primary-50/80 shadow-sm ring-2 ring-primary-500/20 ring-offset-2 ring-offset-background dark:border-primary-400 dark:bg-primary-500/10 dark:shadow-none dark:ring-primary-400/30'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-slate-600 dark:hover:bg-slate-900/70',
        disabled && 'cursor-not-allowed opacity-60',
        className
      )}
    >
      {icon ? (
        iconContainerClassName ? (
          <span className={iconContainerClassName} aria-hidden>
            {icon}
          </span>
        ) : (
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-lg',
              selected
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/60 dark:text-primary-300'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
            )}
            aria-hidden
          >
            {icon}
          </span>
        )
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-slate-900 dark:text-white">{title}</span>
          {badge ? (
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700 dark:bg-primary-900/50 dark:text-primary-300">
              {badge}
            </span>
          ) : null}
        </span>
        {description ? (
          <span className="mt-1 block text-sm leading-snug text-slate-600 dark:text-slate-400">
            {description}
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          selected
            ? 'border-primary-600 bg-primary-600 text-white dark:border-primary-400 dark:bg-primary-500'
            : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-900'
        )}
        aria-hidden
      >
        {selected ? <Check className="h-3 w-3" /> : null}
      </span>
    </button>
  );
}

export interface SelectableCardGridProps {
  children: React.ReactNode;
  label: string;
  className?: string;
  /** Matches card selectionMode. Default: single (radiogroup). */
  selectionMode?: 'single' | 'multiple';
}

export function SelectableCardGrid({
  children,
  label,
  className,
  selectionMode = 'single',
}: SelectableCardGridProps) {
  return (
    <div
      role={selectionMode === 'multiple' ? 'group' : 'radiogroup'}
      aria-label={label}
      className={cn('grid gap-3 sm:grid-cols-2', className)}
    >
      {children}
    </div>
  );
}
